import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'

export default function VanForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEdit = !!id

  const [loading, setLoading] = useState(isEdit)
  const [errors, setErrors] = useState([])
  const [attachments, setAttachments] = useState([])
  const [uploadingFile, setUploadingFile] = useState(false)
  const [fetchingQuickBooks, setFetchingQuickBooks] = useState(false)
  const [formData, setFormData] = useState({
    serial_number: '',
    name: '',
    vin: '',
    own_or_upfit: 'Upfit',
    vehicle_purchase_price: '',
    loan_balance: '',
    retail_sale_price: '',
    date_acquired: '',
    customer_name: '',
    delivery_date: '',
    sold: false,
    date_sold: '',
    quickbooks_invoice_number: '',
    notes: '',
    lender: ''
  })

  useEffect(() => {
    if (isEdit) {
      fetch(`/api/vans/${id}`)
        .then(response => response.json())
        .then(data => {
          setFormData({
            serial_number: data.serial_number || '',
            name: data.name || '',
            vin: data.vin || '',
            own_or_upfit: data.own_or_upfit || 'Upfit',
            vehicle_purchase_price: data.vehicle_purchase_price || '',
            loan_balance: data.loan_balance || '',
            retail_sale_price: data.retail_sale_price || '',
            date_acquired: data.date_acquired || '',
            customer_name: data.customer_name || '',
            delivery_date: data.delivery_date || '',
            sold: data.sold || false,
            date_sold: data.date_sold || '',
            quickbooks_invoice_number: data.quickbooks_invoice_number || '',
            notes: data.notes || '',
            lender: data.lender || ''
          })
          setAttachments(data.attachments || [])
          setLoading(false)
        })
        .catch(err => {
          console.error('Error fetching van:', err)
          setLoading(false)
        })
    }
  }, [id, isEdit])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    })
  }

  // Calculate estimated interest paid
  const calculateEstimatedInterest = () => {
    const dateAcquired = formData.date_acquired
    const purchasePrice = parseFloat(formData.vehicle_purchase_price)

    if (!dateAcquired || !purchasePrice || isNaN(purchasePrice)) {
      return 'N/A'
    }

    const acquired = new Date(dateAcquired)
    const today = new Date()
    const daysDiff = Math.floor((today - acquired) / (1000 * 60 * 60 * 24))

    // interest = (days_diff / 365) * purchase_price * 0.08
    const interest = (daysDiff / 365) * purchasePrice * 0.08

    return `$${interest.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }

  // Handle file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'image/png',
      'image/jpeg',
      'image/jpg',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ]

    if (!allowedTypes.includes(file.type)) {
      alert('Please upload a PDF, image (PNG/JPG), or Excel file (.xls/.xlsx)')
      return
    }

    setUploadingFile(true)
    const formData = new FormData()
    formData.append('file', file)

    try {
      const response = await fetch(`/api/vans/${id}/upload_attachment`, {
        method: 'POST',
        body: formData
      })

      const data = await response.json()
      if (data.success) {
        setAttachments(data.attachments)
        e.target.value = '' // Reset file input
      } else {
        alert('Failed to upload file: ' + (data.error || 'Unknown error'))
      }
    } catch (err) {
      console.error('Error uploading file:', err)
      alert('Failed to upload file')
    } finally {
      setUploadingFile(false)
    }
  }

  // Handle file delete
  const handleFileDelete = async (attachmentId) => {
    if (!confirm('Are you sure you want to delete this file?')) return

    try {
      const response = await fetch(`/api/vans/${id}/delete_attachment/${attachmentId}`, {
        method: 'DELETE'
      })

      const data = await response.json()
      if (data.success) {
        setAttachments(data.attachments)
      } else {
        alert('Failed to delete file: ' + (data.error || 'Unknown error'))
      }
    } catch (err) {
      console.error('Error deleting file:', err)
      alert('Failed to delete file')
    }
  }

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  }

  // Handle QuickBooks invoice fetch
  const handleFetchQuickBooksInvoice = async () => {
    if (!formData.quickbooks_invoice_number) {
      alert('Please enter an invoice number first')
      return
    }

    setFetchingQuickBooks(true)

    try {
      const response = await fetch('/api/vans/fetch_quickbooks_invoice', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ invoice_number: formData.quickbooks_invoice_number })
      })

      const data = await response.json()

      if (data.success) {
        // Update form with fetched data
        setFormData({
          ...formData,
          retail_sale_price: data.subtotal || formData.retail_sale_price,
          notes: data.notes || formData.notes,
          customer_name: data.customer_name || formData.customer_name
        })
        alert(`Successfully fetched invoice data!\nSubtotal: $${data.subtotal?.toFixed(2)}\nCustomer: ${data.customer_name}`)
      } else {
        alert(`Failed to fetch invoice: ${data.error || 'Unknown error'}`)
      }
    } catch (err) {
      console.error('Error fetching QuickBooks invoice:', err)
      alert('Failed to fetch invoice data from QuickBooks')
    } finally {
      setFetchingQuickBooks(false)
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setErrors([])

    const url = isEdit ? `/api/vans/${id}` : '/api/vans'
    const method = isEdit ? 'PUT' : 'POST'

    fetch(url, {
      method: method,
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ van: formData })
    })
      .then(response => response.json())
      .then(data => {
        if (data.errors) {
          setErrors(data.errors)
        } else {
          navigate('/vans')
        }
      })
      .catch(err => {
        console.error('Error saving van:', err)
        setErrors(['An error occurred while saving the van'])
      })
  }

  if (loading) {
    return (
      <div className="px-4 sm:px-0">
        <div className="flex justify-center items-center h-96">
          <div className="text-gray-500">Loading...</div>
        </div>
      </div>
    )
  }

  return (
    <div className="px-4 sm:px-0">
      <div className="mb-6">
        <Link
          to="/vans"
          className="text-sm text-gray-600 hover:text-gray-900 mb-2 inline-block"
        >
          ← Back to Inventory
        </Link>
        <h1 className="text-3xl font-bold text-gray-900">
          {isEdit ? 'Edit Van' : 'Add New Van'}
        </h1>
      </div>

      {errors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <h3 className="text-sm font-medium text-red-800 mb-2">
            Please fix the following errors:
          </h3>
          <ul className="list-disc list-inside text-sm text-red-700">
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white shadow rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Serial Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Serial Number *
            </label>
            <input
              type="text"
              name="serial_number"
              value={formData.serial_number}
              onChange={handleChange}
              placeholder="#0001"
              required
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
            />
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Van Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., White Transit 170"
              required
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
            />
          </div>

          {/* VIN */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              VIN
            </label>
            <input
              type="text"
              name="vin"
              value={formData.vin}
              onChange={handleChange}
              placeholder="17-character VIN"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
            />
          </div>

          {/* Own or Upfit */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type *
            </label>
            <select
              name="own_or_upfit"
              value={formData.own_or_upfit}
              onChange={handleChange}
              required
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
            >
              <option value="Upfit">Upfit (Customer Van)</option>
              <option value="Own">Own (WV Purchased Van)</option>
            </select>
          </div>

          {/* Vehicle Purchase Price */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Vehicle Purchase Price
            </label>
            <input
              type="number"
              name="vehicle_purchase_price"
              value={formData.vehicle_purchase_price}
              onChange={handleChange}
              placeholder="0"
              step="0.01"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
            />
          </div>

          {/* Loan Balance */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Loan Balance
            </label>
            <input
              type="number"
              name="loan_balance"
              value={formData.loan_balance}
              onChange={handleChange}
              placeholder="0"
              step="0.01"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
            />
          </div>

          {/* Retail Sale Price */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Retail Sale Price *
            </label>
            <input
              type="number"
              name="retail_sale_price"
              value={formData.retail_sale_price}
              onChange={handleChange}
              placeholder="0"
              step="0.01"
              required
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
            />
          </div>

          {/* Date Acquired */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date Acquired *
            </label>
            <input
              type="date"
              name="date_acquired"
              value={formData.date_acquired}
              onChange={handleChange}
              required
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
            />
          </div>

          {/* Delivery Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Delivery Date
            </label>
            <input
              type="date"
              name="delivery_date"
              value={formData.delivery_date}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
            />
          </div>

          {/* Customer Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Customer Name {formData.sold && '*'}
            </label>
            <input
              type="text"
              name="customer_name"
              value={formData.customer_name}
              onChange={handleChange}
              placeholder="Customer name"
              required={formData.sold}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
            />
          </div>

          {/* QuickBooks Invoice Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              QuickBooks Invoice #
            </label>
            <div className="flex space-x-2">
              <input
                type="text"
                name="quickbooks_invoice_number"
                value={formData.quickbooks_invoice_number}
                onChange={handleChange}
                placeholder="INV-1234"
                className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
              />
              <button
                type="button"
                onClick={handleFetchQuickBooksInvoice}
                disabled={fetchingQuickBooks || !formData.quickbooks_invoice_number}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {fetchingQuickBooks ? 'Fetching...' : 'Fetch QB Info'}
              </button>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Click "Fetch QB Info" to immediately fetch invoice data, or it will be automatically fetched when you save.
            </p>
          </div>

          {/* Lender */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Lender
            </label>
            <input
              type="text"
              name="lender"
              value={formData.lender}
              onChange={handleChange}
              placeholder="e.g., Wells Fargo, Chase Bank"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
            />
          </div>
        </div>

        {/* Estimated Interest Paid - View Mode Only */}
        {isEdit && (
          <div className="mt-6 p-4 bg-gray-50 rounded-md border border-gray-200">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-700">Estimated Interest Paid</span>
              <span className="text-lg font-bold text-gray-900">{calculateEstimatedInterest()}</span>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Calculated as: (days owned / 365) × purchase price × 8% annual rate
            </p>
          </div>
        )}

        {/* Notes Section - Full Width */}
        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Notes
          </label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows="4"
            placeholder="Invoice line items will automatically populate here when you enter a QuickBooks Invoice #..."
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
          />
          <p className="mt-1 text-xs text-gray-500">
            This field is automatically populated with QuickBooks invoice line items. You can also add custom notes manually.
          </p>
        </div>

        {/* Sold Status Section */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="flex items-center mb-4">
            <input
              type="checkbox"
              name="sold"
              checked={formData.sold}
              onChange={handleChange}
              className="h-4 w-4 text-gray-900 focus:ring-gray-900 border-gray-300 rounded"
            />
            <label className="ml-2 block text-sm font-medium text-gray-700">
              Mark as Sold
            </label>
          </div>

          {formData.sold && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date Sold *
                </label>
                <input
                  type="date"
                  name="date_sold"
                  value={formData.date_sold}
                  onChange={handleChange}
                  required={formData.sold}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
                />
              </div>
            </div>
          )}
        </div>

        {/* File Attachments Section - Only show in edit mode */}
        {isEdit && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">File Attachments</h3>

            {/* File Upload */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload File (PDF, Images, Excel)
              </label>
              <input
                type="file"
                onChange={handleFileUpload}
                accept=".pdf,.png,.jpg,.jpeg,.xls,.xlsx"
                disabled={uploadingFile}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
              />
              {uploadingFile && (
                <p className="mt-1 text-xs text-gray-500">Uploading...</p>
              )}
            </div>

            {/* Attachments List */}
            {attachments.length > 0 ? (
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Uploaded Files ({attachments.length})
                </p>
                {attachments.map((attachment) => (
                  <div
                    key={attachment.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-md border border-gray-200"
                  >
                    <div className="flex items-center space-x-3 flex-1">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {attachment.filename}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatFileSize(attachment.byte_size)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <a
                        href={attachment.url}
                        download
                        className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-xs font-medium text-gray-700 bg-white hover:bg-gray-50"
                      >
                        Download
                      </a>
                      <button
                        type="button"
                        onClick={() => handleFileDelete(attachment.id)}
                        className="inline-flex items-center px-3 py-1 border border-red-300 rounded-md text-xs font-medium text-red-700 bg-white hover:bg-red-50"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No files uploaded yet.</p>
            )}
          </div>
        )}

        {/* Form Actions */}
        <div className="mt-6 flex space-x-3">
          <button
            type="submit"
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900"
          >
            {isEdit ? 'Update Van' : 'Create Van'}
          </button>
          <Link
            to="/vans"
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900"
          >
            Cancel
          </Link>
        </div>
      </form>
    </div>
  )
}
